import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { ArrowLeft, AlertTriangle } from 'lucide-react';
import {
    postFortnoxSyncSalary,
    postFortnoxSyncAbsences,
    postFortnoxSyncExpenses,
    postFortnoxSyncBilling,
    postFortnoxSyncReceipts,
} from '../../lib/api';

const MONTH_VALUES = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const currentYear = new Date().getFullYear();
const YEARS = [currentYear - 1, currentYear, currentYear + 1];

const SYNC_TYPES = [
    { key: 'salary',   fn: postFortnoxSyncSalary },
    { key: 'absences', fn: postFortnoxSyncAbsences },
    { key: 'expenses', fn: postFortnoxSyncExpenses },
    { key: 'billing',  fn: postFortnoxSyncBilling },
    { key: 'receipts', fn: postFortnoxSyncReceipts },
];

/**
 * FortnoxSyncDialog — two-step dialog for syncing data to Fortnox.
 *
 * Step 1: Select month/year and which sync types to include, then preview.
 * Step 2: Review preview results per type, then confirm real sync.
 *
 * Props:
 *   open         {boolean}  — controlled open state
 *   onOpenChange {function} — called with boolean when dialog should open/close
 *   defaultMonth {number}  — 1–12, the month pre-selected in Step 1
 *   defaultYear  {number}  — the year pre-selected in Step 1
 */
export default function FortnoxSyncDialog({ open, onOpenChange, defaultMonth, defaultYear }) {
    const { t } = useTranslation();

    // ── Step state ──────────────────────────────────────────────────────────────
    const [step, setStep] = useState(1);

    // ── Step 1 state ────────────────────────────────────────────────────────────
    const [selectedMonth, setSelectedMonth] = useState(defaultMonth ?? new Date().getMonth() + 1);
    const [selectedYear,  setSelectedYear]  = useState(defaultYear  ?? new Date().getFullYear());

    const [includeSalary,   setIncludeSalary]   = useState(true);
    const [includeAbsences, setIncludeAbsences] = useState(true);
    const [includeExpenses, setIncludeExpenses] = useState(true);
    const [includeBilling,  setIncludeBilling]  = useState(true);
    const [includeReceipts, setIncludeReceipts] = useState(true);

    const [previewing, setPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState('');

    // ── Step 2 state ────────────────────────────────────────────────────────────
    // Map of sync_type -> FortnoxSyncResult
    const [previewResults, setPreviewResults] = useState({});
    // Map of sync_type -> FortnoxSyncResult (after real sync)
    const [syncResults, setSyncResults] = useState({});
    // Map of sync_type -> boolean (per-row loading state)
    const [syncingRows, setSyncingRows] = useState({});
    const [syncing, setSyncing] = useState(false);
    const [syncDone, setSyncDone] = useState(false);

    // ── Helpers ─────────────────────────────────────────────────────────────────
    const selectedTypes = SYNC_TYPES.filter(({ key }) => {
        if (key === 'salary')   return includeSalary;
        if (key === 'absences') return includeAbsences;
        if (key === 'expenses') return includeExpenses;
        if (key === 'billing')  return includeBilling;
        if (key === 'receipts') return includeReceipts;
        return false;
    });

    const anyChecked = selectedTypes.length > 0;

    // ── Reset on open ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (open) {
            setStep(1);
            setSelectedMonth(defaultMonth ?? new Date().getMonth() + 1);
            setSelectedYear(defaultYear   ?? new Date().getFullYear());
            setIncludeSalary(true);
            setIncludeAbsences(true);
            setIncludeExpenses(true);
            setIncludeBilling(true);
            setIncludeReceipts(true);
            setPreviewing(false);
            setPreviewError('');
            setPreviewResults({});
            setSyncResults({});
            setSyncingRows({});
            setSyncing(false);
            setSyncDone(false);
        }
    }, [open, defaultMonth, defaultYear]);

    // ── Handlers ────────────────────────────────────────────────────────────────
    const handlePreview = async () => {
        setPreviewing(true);
        setPreviewError('');
        setPreviewResults({});

        const payload = { month: selectedMonth, year: selectedYear, dry_run: true };

        try {
            const results = await Promise.allSettled(
                selectedTypes.map(({ fn }) => fn(payload))
            );
            const mapped = {};
            selectedTypes.forEach(({ key }, i) => {
                const result = results[i];
                if (result.status === 'fulfilled') {
                    mapped[key] = result.value.data;
                } else {
                    mapped[key] = {
                        validation_errors: [{ error: result.reason?.message || 'Preview failed' }],
                        transactions_created: 0,
                        skipped_already_synced: 0,
                        preview: [],
                    };
                }
            });
            setPreviewResults(mapped);
            setStep(2);
        } catch {
            setPreviewError(t('reports.fortnox_preview_error', 'Preview failed. Please try again.'));
        } finally {
            setPreviewing(false);
        }
    };

    const handleConfirmSync = async () => {
        setSyncing(true);
        setSyncResults({});

        // Only sync types that have zero validation_errors
        const typesToSync = selectedTypes.filter(({ key }) => {
            const preview = previewResults[key];
            return preview && (!preview.validation_errors || preview.validation_errors.length === 0);
        });

        const payload = { month: selectedMonth, year: selectedYear, dry_run: false };

        for (const { key, fn } of typesToSync) {
            setSyncingRows(prev => ({ ...prev, [key]: true }));
            try {
                const res = await fn(payload);
                setSyncResults(prev => ({ ...prev, [key]: res.data }));
            } catch {
                setSyncResults(prev => ({
                    ...prev,
                    [key]: { error: true, sync_type: key },
                }));
            } finally {
                setSyncingRows(prev => ({ ...prev, [key]: false }));
            }
        }

        setSyncing(false);
        setSyncDone(true);
    };

    const handleBack = () => {
        setStep(1);
        setPreviewResults({});
        setSyncResults({});
        setSyncingRows({});
        setSyncing(false);
        setSyncDone(false);
        setPreviewError('');
    };

    const handleClose = () => {
        onOpenChange(false);
    };

    // ── Derived for Step 2 ───────────────────────────────────────────────────────
    const anyHasErrors = selectedTypes.some(({ key }) => {
        const preview = previewResults[key];
        return preview?.validation_errors?.length > 0;
    });

    // ── Render ──────────────────────────────────────────────────────────────────
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg">
                {step === 1 ? (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t('reports.fortnox_sync_title')}</DialogTitle>
                            <DialogDescription>
                                {t('reports.fortnox_sync_description')}
                            </DialogDescription>
                        </DialogHeader>

                        <div className="space-y-5 py-2">
                            {/* Month / Year selectors */}
                            <div className="flex gap-3">
                                <div className="flex-1 space-y-1.5">
                                    <Label htmlFor="fortnox-sync-month">{t('email_dialog.month_label')}</Label>
                                    <Select
                                        value={String(selectedMonth)}
                                        onValueChange={(v) => setSelectedMonth(Number(v))}
                                    >
                                        <SelectTrigger id="fortnox-sync-month">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {MONTH_VALUES.map((m) => (
                                                <SelectItem key={m} value={String(m)}>
                                                    {t(`email_dialog.months.${m}`)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex-1 space-y-1.5">
                                    <Label htmlFor="fortnox-sync-year">{t('email_dialog.year_label')}</Label>
                                    <Select
                                        value={String(selectedYear)}
                                        onValueChange={(v) => setSelectedYear(Number(v))}
                                    >
                                        <SelectTrigger id="fortnox-sync-year">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {YEARS.map((y) => (
                                                <SelectItem key={y} value={String(y)}>
                                                    {y}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Sync type checkboxes */}
                            <div className="space-y-3">
                                <p className="text-sm font-medium">{t('email_dialog.attachments_label')}</p>

                                {[
                                    { id: 'sync-salary',   checked: includeSalary,   onChange: (c) => setIncludeSalary(!!c),   labelKey: 'reports.fortnox_sync_salary' },
                                    { id: 'sync-absences', checked: includeAbsences, onChange: (c) => setIncludeAbsences(!!c), labelKey: 'reports.fortnox_sync_absences' },
                                    { id: 'sync-expenses', checked: includeExpenses, onChange: (c) => setIncludeExpenses(!!c), labelKey: 'reports.fortnox_sync_expenses' },
                                    { id: 'sync-billing',  checked: includeBilling,  onChange: (c) => setIncludeBilling(!!c),  labelKey: 'reports.fortnox_sync_billing' },
                                    { id: 'sync-receipts', checked: includeReceipts, onChange: (c) => setIncludeReceipts(!!c), labelKey: 'reports.fortnox_sync_receipts' },
                                ].map(({ id, checked, onChange, labelKey }) => (
                                    <div key={id} className="flex items-center gap-2">
                                        <Checkbox
                                            id={id}
                                            checked={checked}
                                            onCheckedChange={onChange}
                                        />
                                        <Label htmlFor={id} className="font-normal cursor-pointer">
                                            {t(labelKey)}
                                        </Label>
                                    </div>
                                ))}
                            </div>

                            {previewError && (
                                <p className="text-sm text-destructive">{previewError}</p>
                            )}
                        </div>

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button variant="ghost" onClick={handleClose} disabled={previewing}>
                                {t('email_dialog.cancel')}
                            </Button>
                            <Button onClick={handlePreview} disabled={!anyChecked || previewing}>
                                {previewing ? (
                                    <span className="flex items-center gap-2">
                                        <span className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                        {t('reports.fortnox_preview_button')}
                                    </span>
                                ) : t('reports.fortnox_preview_button')}
                            </Button>
                        </DialogFooter>
                    </>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                {!syncDone && (
                                    <button
                                        type="button"
                                        onClick={handleBack}
                                        className="rounded p-0.5 hover:bg-muted transition-colors"
                                        aria-label={t('email_dialog.back_aria')}
                                        disabled={syncing}
                                    >
                                        <ArrowLeft className="w-4 h-4" />
                                    </button>
                                )}
                                {syncDone
                                    ? t('reports.fortnox_sync_complete')
                                    : t('reports.fortnox_sync_title')}
                            </DialogTitle>
                            {!syncDone && (
                                <DialogDescription>
                                    {t('reports.fortnox_sync_description')}
                                </DialogDescription>
                            )}
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            {/* Warning banner if any type has errors */}
                            {!syncDone && anyHasErrors && (
                                <div className="flex items-start gap-2 rounded border border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-3 text-sm text-amber-800 dark:text-amber-300">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>{t('reports.fortnox_errors_will_skip')}</span>
                                </div>
                            )}

                            {/* Results table */}
                            <div className="overflow-auto max-h-[55vh]">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b">
                                            <th className="text-left py-2 pr-3 font-semibold">{t('common.type', 'Type')}</th>
                                            <th className="text-right py-2 px-2 font-semibold">
                                                {syncDone ? t('reports.fortnox_created') : t('reports.fortnox_to_create')}
                                            </th>
                                            <th className="text-right py-2 px-2 font-semibold">{t('reports.fortnox_validation_errors')}</th>
                                            <th className="text-right py-2 pl-2 font-semibold">{t('reports.fortnox_already_synced')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedTypes.map(({ key }) => {
                                            const preview = previewResults[key] || {};
                                            const final   = syncResults[key];
                                            const isRowSyncing = !!syncingRows[key];
                                            const errCount  = preview.validation_errors?.length ?? 0;
                                            const skipCount = preview.skipped_already_synced ?? 0;
                                            const toCreate  = preview.transactions_created ?? preview.preview?.length ?? 0;
                                            const skippedDueToError = errCount > 0;

                                            return (
                                                <tr key={key} className="border-b last:border-0 hover:bg-muted/40">
                                                    <td className="py-2 pr-3 font-medium">{t(`reports.fortnox_sync_${key}`)}</td>
                                                    <td className="py-2 px-2 text-right">
                                                        {isRowSyncing ? (
                                                            <span className="inline-block w-4 h-4 border border-current border-t-transparent rounded-full animate-spin" />
                                                        ) : final?.error ? (
                                                            <Badge variant="destructive" className="text-xs">—</Badge>
                                                        ) : final ? (
                                                            <Badge variant="default" className="text-xs bg-green-600 hover:bg-green-600">
                                                                {final.transactions_created ?? 0}
                                                            </Badge>
                                                        ) : skippedDueToError ? (
                                                            <span className="text-muted-foreground text-xs">{t('reports.fortnox_skipped')}</span>
                                                        ) : (
                                                            <Badge variant="outline" className="text-xs text-green-700 border-green-300 bg-green-50 dark:bg-green-950/30 dark:text-green-400 dark:border-green-700">
                                                                {toCreate}
                                                            </Badge>
                                                        )}
                                                    </td>
                                                    <td className="py-2 px-2 text-right">
                                                        {errCount > 0 ? (
                                                            <div className="space-y-1">
                                                                <Badge variant="destructive" className="text-xs">
                                                                    {errCount}
                                                                </Badge>
                                                                <ul className="text-xs text-destructive text-left list-disc list-inside space-y-0.5 mt-1">
                                                                    {preview.validation_errors.map((e, i) => (
                                                                        <li key={i}>{e.error ?? String(e)}</li>
                                                                    ))}
                                                                </ul>
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted-foreground text-xs">—</span>
                                                        )}
                                                    </td>
                                                    <td className="py-2 pl-2 text-right">
                                                        {skipCount > 0 ? (
                                                            <Badge variant="outline" className="text-xs text-amber-700 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:text-amber-400 dark:border-amber-700">
                                                                {skipCount}
                                                            </Badge>
                                                        ) : (
                                                            <span className="text-muted-foreground text-xs">—</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <DialogFooter className="gap-2 sm:gap-0">
                            <Button variant="ghost" onClick={handleClose} disabled={syncing}>
                                {t('email_dialog.cancel')}
                            </Button>
                            {!syncDone && (
                                <Button
                                    onClick={handleConfirmSync}
                                    disabled={syncing}
                                >
                                    {syncing ? t('reports.fortnox_syncing') : t('reports.fortnox_confirm_sync')}
                                </Button>
                            )}
                        </DialogFooter>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
